<template>
  <layout>
    <div
      class="space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.permissions.title") }}
        </h1>
        <template v-if="hasPermission(permissions.createRole)">
          <primary-button
            class="hidden sm:flex"
            :label="t('admin.settings.permissions.add_role')"
            @click="openSlideOver"
          />
          <PlusIcon
            class="w-10 text-gray-900 sm:hidden -mr-1"
            @click="openSlideOver"
          />
        </template>
      </div>
    </div>
    <div class="flex flex-col dynamic-height overflow-auto">
      <Role
        v-for="role in roles"
        :key="role.id"
        :role="role"
        :selected="selectedRole?.id === role.id"
        :open="openRole?.id === role.id"
        :disabled="!hasPermission(permissions.updateRole)"
        @selected="handleSelected"
        @update="handleUpdateRole"
        @toggle="handleToggleRole"
      />
    </div>

    <settings-panel
      :open="openRoleSlideOver"
      :details="selectedRole"
      :fields="roleFields"
      :canCreate="hasPermission(permissions.createRole)"
      :canUpdate="hasPermission(permissions.updateRole)"
      :canDelete="hasPermission(permissions.deleteRole)"
      :is-create-loading="isCreateLoading"
      :is-update-loading="isUpdateLoading"
      :is-delete-loading="isDeleteLoading"
      :createLabel="t('admin.settings.permissions.add_role')"
      :updateLabel="t('admin.settings.permissions.update_role')"
      :deleteLabel="t('admin.settings.permissions.delete_role')"
      :deleteConfirmation="{
        message: [t('admin.settings.permissions.confirmation.message')],
      }"
      @close="closeSlideOver"
      @create="handleCreateRole"
      @update="handleUpdateRole"
      @delete="handleDeleteRole"
    />
  </layout>
</template>

<script setup>
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import Layout from "@/Tailwind/Layouts/TenantSettings";
import { computed, inject, ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { usePage } from "@inertiajs/vue3";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import SettingsPanel from "@/Tailwind/Components/Settings/SettingsPanel.vue";
import { useI18n } from "vue-i18n";
import Role from "@/Tailwind/Components/Settings/Permissions/Role.vue";

const { t } = useI18n();
const { hasPermission, permissions } = inject("permissions");

const roles = computed(() => usePage().props.roles);

const tenant = usePage().props.tenant;
const openRoleSlideOver = ref(false);
const selectedRole = ref(null);
const openRole = ref(null);

const isCreateLoading = ref(false);
const isUpdateLoading = ref(false);
const isDeleteLoading = ref(false);

defineOptions({ layout: PersistentLayout });

// const handleCloseIconSelector = () =>
//   router.visit(route("settings.categories.index", [tenant]));

function handleBack() {
  router.visit(route("settings.tenant", [tenant]));
}

// Role management
const roleFields = ref([
  {
    key: "name",
    title: t("admin.settings.permissions.role_name.title"),
    description: t("admin.settings.permissions.role_name.description"),
    placeholder: t("admin.settings.permissions.role_name.placeholder"),
    type: "text",
  },
  {
    key: "description",
    title: t("admin.settings.permissions.role_description.title"),
    description: t("admin.settings.permissions.role_description.description"),
    placeholder: t("admin.settings.permissions.role_description.placeholder"),
    type: "textarea",
  },
]);

const openSlideOver = () => {
  selectedRole.value = null;
  openRoleSlideOver.value = true;
};

const closeSlideOver = () => {
  selectedRole.value = null;
  openRoleSlideOver.value = false;
  isCreateLoading.value = false;
  isUpdateLoading.value = false;
  isDeleteLoading.value = false;
};

const handleSelected = (row) => {
  selectedRole.value = row;
  openRoleSlideOver.value = true;
};

const handleToggleRole = (role) => {
  if (openRole.value === role) {
    openRole.value = null;

    return;
  }

  openRole.value = role;
};

const handleCreateRole = (inertiaForm) => {
  inertiaForm.post(route("settings.roles.store", { tenant: tenant.id }), {
    onStart: () => (isCreateLoading.value = true),
    onSuccess: () => {
      closeSlideOver();
    },
    onError: () => (isCreateLoading.value = false),
  });
};

const handleUpdateRole = (inertiaForm) => {
  inertiaForm.put(
    route("settings.roles.update", {
      role: selectedRole.value?.id ?? openRole.value?.id,
      tenant: tenant.id,
    }),
    {
      onStart: () => (isUpdateLoading.value = true),
      onSuccess: () => {
        closeSlideOver();
      },
      onError: () => (isUpdateLoading.value = false),
    }
  );
};

const handleDeleteRole = () => {
  router.delete(
    route("settings.roles.destroy", {
      role: selectedRole.value.id,
      tenant: tenant.id,
    }),
    {
      onStart: () => (isDeleteLoading.value = true),
      onSuccess: () => {
        closeSlideOver();
      },
      onError: () => (isDeleteLoading.value = false),
    }
  );
};
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 87px);
}
</style>
